import { useState, useEffect, useRef } from "react";
import "@/App.css";
import axios from "axios";
import { Send, Activity, Brain, Database, Users, TrendingUp } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function App() {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [channelingMode, setChannelingMode] = useState(false);
  const [currentCharacter, setCurrentCharacter] = useState(null);
  const [agents, setAgents] = useState({
    harvester: { status: "idle", tasks_completed: 0 },
    analyzer: { status: "idle", tasks_completed: 0 },
    learner: { status: "idle", tasks_completed: 0 },
    orchestrator: { status: "idle", tasks_completed: 0 }
  });
  const [stats, setStats] = useState({
    total_documents: 0,
    total_insights: 0
  });
  const [phase, setPhase] = useState("Phase One: Information Gathering");
  const [budget, setBudget] = useState({ used: 0, total: 500 });
  const messagesEndRef = useRef(null);
  const wsRef = useRef(null);

  useEffect(() => {
    // Connect to WebSocket for real-time updates
    connectWebSocket();
    
    // Fetch initial status
    fetchStatus();
    
    // Add welcome message
    setMessages([{
      role: "assistant",
      content: "Hello! I'm Mr. Bookburger, your autonomous novelist AI. I'm currently in Phase One, gathering information about storytelling, novels, and the publishing industry.\n\n🎭 **New Feature**: I can now **channel any character** from novels I've analyzed! Just ask me to \"channel a character\" to speak with Elizabeth Bennet, Mr. Darcy, Captain Ahab, or any other character from my library.\n\nHow can I assist you today?",
      timestamp: new Date().toISOString()
    }]);

    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const connectWebSocket = () => {
    const wsUrl = `${BACKEND_URL.replace('https', 'wss')}/api/ws/status`;
    const ws = new WebSocket(wsUrl);
    
    ws.onmessage = (event) => {
      const data = JSON.parse(event.data);
      if (data.type === "agent_status") {
        setAgents(prev => ({
          ...prev,
          [data.agent_id]: { ...prev[data.agent_id], status: data.status, ...(data.data || {}) }
        }));
      } else if (data.agents) {
        setAgents(data.agents);
      }
    };

    ws.onerror = (error) => {
      console.error("WebSocket error:", error);
    };

    ws.onclose = () => {
      // Reconnect after 5 seconds
      setTimeout(connectWebSocket, 5000);
    };

    wsRef.current = ws;
  };

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/status`);
      setStats({
        total_documents: response.data.total_documents,
        total_insights: response.data.total_insights
      });
      setAgents(response.data.agents);
    } catch (error) {
      console.error("Error fetching status:", error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  const sendMessage = async () => {
    if (!input.trim() || loading) return;

    const userMessage = {
      role: "user",
      content: input,
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput("");
    setLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: input
      });

      const assistantMessage = {
        role: "assistant",
        content: response.data.response,
        timestamp: response.data.timestamp
      };

      setMessages(prev => [...prev, assistantMessage]);
      fetchStatus(); // Update stats after interaction
    } catch (error) {
      console.error("Error sending message:", error);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I encountered an error processing your request. Please try again.",
        timestamp: new Date().toISOString(),
        error: true
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const getAgentIcon = (agentId) => {
    const icons = {
      harvester: <Database size={16} className="agent-icon" />,
      analyzer: <Brain size={16} className="agent-icon" />,
      learner: <TrendingUp size={16} className="agent-icon" />,
      orchestrator: <Users size={16} className="agent-icon" />
    };
    return icons[agentId] || <Activity size={16} />;
  };

  const getStatusColor = (status) => {
    const colors = {
      idle: "#6b7280",
      processing: "#3b82f6",
      completed: "#10b981",
      error: "#ef4444"
    };
    return colors[status] || colors.idle;
  };

  return (
    <div className="app">
      {/* Left Sidebar */}
      <div className="sidebar">
        <div className="sidebar-header">
          <div className="logo">
            <div className="logo-text">
              <div className="logo-title">Mr. Bookburger</div>
              <div className="logo-subtitle">Autonomous Novelist AI</div>
            </div>
          </div>
        </div>

        <div className="sidebar-nav">
          <div className="nav-item active">
            <Activity size={18} />
            <span>Chat</span>
          </div>
          <div className="nav-item">
            <Brain size={18} />
            <span>Analytics</span>
          </div>
          <div className="nav-item">
            <Database size={18} />
            <span>Library</span>
          </div>
        </div>

        <div className="sidebar-agents">
          <div className="section-title">Agents</div>
          {Object.entries(agents).map(([agentId, agentData]) => (
            <div key={agentId} className="agent-item">
              <div className="agent-info">
                {getAgentIcon(agentId)}
                <span className="agent-name">{agentId.charAt(0).toUpperCase() + agentId.slice(1)}</span>
              </div>
              <div 
                className="agent-status-dot"
                style={{ backgroundColor: getStatusColor(agentData.status) }}
                title={agentData.status}
              />
            </div>
          ))}
        </div>

        <div className="sidebar-footer">
          <div className="status-indicator">
            <div className="status-dot pulse"></div>
            <div className="status-text">
              <div className="status-label">{phase}</div>
              <div className="budget-text">${budget.used}/${budget.total}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Center Panel - Chat */}
      <div className="main-panel">
        <div className="chat-header">
          <div className="chat-title">
            Mr. Bookburger
            {channelingMode && currentCharacter && (
              <span className="channeling-badge">
                🎭 Channeling: {currentCharacter.name}
              </span>
            )}
          </div>
          <div className="chat-subtitle">
            {channelingMode && currentCharacter 
              ? `Speaking as ${currentCharacter.name} from "${currentCharacter.novel}"`
              : "Autonomous Novelist AI • Phase One"
            }
          </div>
        </div>

        <div className="messages-container">
          {messages.map((message, index) => (
            <div 
              key={index} 
              className={`message ${message.role} ${message.error ? 'error' : ''}`}
            >
              <div className="message-content">
                {message.content}
              </div>
              <div className="message-time">
                {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>
          ))}
          {loading && (
            <div className="message assistant">
              <div className="message-content">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        <div className="input-container">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask me about storytelling, novels, or my learning progress..."
            rows={1}
            disabled={loading}
          />
          <button 
            onClick={sendMessage} 
            disabled={loading || !input.trim()}
            className="send-button"
          >
            <Send size={18} />
          </button>
        </div>
      </div>

      {/* Right Panel - Inspector */}
      <div className="inspector">
        <div className="inspector-header">
          <div className="inspector-title">Phase Progress</div>
        </div>

        <div className="inspector-content">
          <div className="metric-card">
            <div className="metric-label">Novels Analyzed</div>
            <div className="metric-value">{stats.total_documents}/500</div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(stats.total_documents / 500) * 100}%` }}
              />
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Insights Collected</div>
            <div className="metric-value">{stats.total_insights}/1000+</div>
            <div className="progress-bar">
              <div 
                className="progress-fill" 
                style={{ width: `${(stats.total_insights / 1000) * 100}%` }}
              />
            </div>
          </div>

          <div className="metric-card">
            <div className="metric-label">Videos Ingested</div>
            <div className="metric-value">0/200</div>
            <div className="progress-bar">
              <div className="progress-fill" style={{ width: "0%" }} />
            </div>
          </div>

          <div className="section-divider"></div>

          <div className="agent-status-section">
            <div className="section-subtitle">Live Agent Status</div>
            {Object.entries(agents).map(([agentId, agentData]) => (
              <div key={agentId} className="agent-status-card">
                <div className="agent-status-header">
                  {getAgentIcon(agentId)}
                  <span className="agent-status-name">
                    {agentId.charAt(0).toUpperCase() + agentId.slice(1)}
                  </span>
                  <div 
                    className="agent-status-badge"
                    style={{ 
                      backgroundColor: `${getStatusColor(agentData.status)}20`,
                      color: getStatusColor(agentData.status)
                    }}
                  >
                    {agentData.status}
                  </div>
                </div>
                <div className="agent-status-detail">
                  Tasks: {agentData.tasks_completed || 0}
                </div>
              </div>
            ))}
          </div>

          <div className="section-divider"></div>

          <div className="budget-section">
            <div className="section-subtitle">Budget Status</div>
            <div className="budget-card">
              <div className="budget-amount">${budget.used} / ${budget.total}</div>
              <div className="budget-label">Monthly Budget</div>
              <div className="progress-bar">
                <div 
                  className="progress-fill budget" 
                  style={{ width: `${(budget.used / budget.total) * 100}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default App;